import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';
import { blogPosts } from '../data/mock';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const BLOG_SEO = {
  'top-10-things-haridwar': {
    title: 'Top 10 Things to Do in Haridwar | Travel Guide',
    description: 'Discover the top 10 spiritual and tourist attractions in Haridwar.',
  },
  'best-time-visit-haridwar': {
    title: 'Best Time to Visit Haridwar | Weather & Travel Guide',
    description: 'Know the best season and time to visit Haridwar for pilgrimage and sightseeing.',
  },
  'kumbh-mela-guide': {
    title: 'Complete Guide to Kumbh Mela Haridwar | Dates & Tips',
    description: 'Learn everything about Kumbh Mela in Haridwar including history, rituals, and travel tips.',
  },
  'complete-travel-guide-haridwar': {
    title: 'Travel Guide Haridwar | Dates & Tips',
    description: 'Know the best season and time to visit Haridwar for pilgrimage and sightseeing. Discover Mansa Devi & Chandi Devi Temple in Haridwar and its religious importance. Explore the spiritual significance of Har Ki Pauri and Ganga Aarti in Haridwar.',
  },
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen pt-32 pb-20">
        <SEO title="Page Not Found | Hotel Suvidha Deluxe Haridwar" description="The page you are looking for could not be found." />
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-navy-900 mb-4">Blog Post Not Found</h1>
          <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="bg-gold-600 hover:bg-gold-700 text-white">
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Parse markdown-like content to HTML
  const parseContent = (content) => {
    if (!content) return '';
    
    const lines = content.split('\n');
    let html = '';
    let inList = false;
    
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      if (!trimmed) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        return;
      }
      
      // H1
      if (trimmed.startsWith('# ') && !trimmed.startsWith('##')) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<h1 class="text-4xl font-bold text-navy-900 mb-6 font-serif mt-8">${trimmed.substring(2)}</h1>`;
        return;
      }
      
      // H2
      if (trimmed.startsWith('## ') && !trimmed.startsWith('###')) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<h2 class="text-3xl font-bold text-navy-900 mb-4 font-serif mt-8">${trimmed.substring(3)}</h2>`;
        return;
      }
      
      // H3
      if (trimmed.startsWith('### ')) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<h3 class="text-2xl font-bold text-navy-900 mb-3 mt-6">${trimmed.substring(4)}</h3>`;
        return;
      }
      
      // List items
      if (trimmed.startsWith('- ')) {
        if (!inList) {
          html += '<ul class="list-disc list-inside mb-4 space-y-2 text-gray-700">';
          inList = true;
        }
        html += `<li class="mb-2">${trimmed.substring(2)}</li>`;
        return;
      }
      
      // Close list if we hit a non-list item
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      
      // Bold text (standalone)
      if (trimmed.startsWith('**') && trimmed.endsWith('**') && trimmed.length > 4) {
        html += `<p class="text-lg font-bold text-navy-900 mb-4">${trimmed.substring(2, trimmed.length - 2)}</p>`;
        return;
      }
      
      // Process inline formatting
      let processedLine = trimmed
        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-navy-900">$1</strong>')
        .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');
      
      // Regular paragraph
      html += `<p class="text-gray-700 mb-4 leading-relaxed">${processedLine}</p>`;
    });
    
    if (inList) {
      html += '</ul>';
    }
    
    return html;
  };

  const htmlContent = parseContent(post.content);

  const seo = BLOG_SEO[post.slug] || {
    title: `${post.title} | Hotel Suvidha Deluxe Haridwar`,
    description: post.excerpt,
  };

  return (
    <div className="min-h-screen pt-32 pb-20">
      <SEO 
        title={seo.title}
        description={seo.description}
        keywords="haridwar travel guide, char dham yatra, har ki pauri, hotel suvidha deluxe, haridwar sightseeing, best time to visit haridwar"
        canonicalUrl={`https://hotelsuvidhadeluxe.com/blog/${post.slug}`}
      />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: `url(${post.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <Link to="/blog">
            <Button variant="ghost" className="text-white hover:text-gold-400 mb-6">
              <ArrowLeft className="mr-2" size={18} />
              Back to Blog
            </Button>
          </Link>
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-serif">{post.title}</h1>
            <div className="flex items-center gap-4 text-gray-300">
              <span className="flex items-center gap-2">
                <Calendar size={18} />
                {post.date}
              </span>
              <span className="flex items-center gap-2">
                <Clock size={18} />
                {post.readTime || '5 min read'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="container mx-auto px-4 -mt-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden">
            <div className="relative h-96 overflow-hidden">
            <img
              src={post.image}
              alt={post.title}
              loading="lazy"
              width={1200}
              height={400}
              className="w-full h-full object-cover"
            />
            </div>
            <div className="p-8 md:p-12">
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-12 bg-gradient-to-r from-navy-900 to-navy-800 rounded-lg p-8 text-white text-center">
            <h2 className="text-3xl font-bold mb-4 font-serif">Ready to Experience Haridwar?</h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Book your stay at Hotel Suvidha Deluxe and make your spiritual journey comfortable and memorable.
            </p>
            <Link to="/booking">
              <Button size="lg" className="bg-gold-600 hover:bg-gold-700 text-white px-8 py-6 text-lg">
                Book Your Stay Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
